import {
    Dispatch,
    FocusEventHandler,
    KeyboardEventHandler,
    SetStateAction,
    SyntheticEvent,
    RefObject,
    useCallback,
    useRef,
    useState,
} from 'react';

import { findNextFocusableElement, keyboardKeys, keyboardMatch } from '@hh.ru/magritte-common-keyboard';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { NativeFocusEventHandler } from '@hh.ru/magritte-ui-suggest/types';

// Когда инпут находится в фокусе, обрабатываем клавиатурную навигацию один раз и передаем фокус в дроп,
// внутри дропа ей будет заниматься useKeyboardNavigation
const focusSuggestPicker = (event: SyntheticEvent, suggestContainer: HTMLElement | null, direction: 'up' | 'down') => {
    if (suggestContainer) {
        const nextFocusableElement = findNextFocusableElement(suggestContainer, null, direction, 'list');
        if (nextFocusableElement !== null) {
            event.preventDefault();
            nextFocusableElement.focus();
        }
    }
};

export const useSuggestKeyboardNav = (
    suggestContainerRef: RefObject<HTMLElement>,
    inputContainerRef: RefObject<HTMLDivElement>,
    setValueSelectedFromSuggest: Dispatch<SetStateAction<string | false>>,
    actualInputValue: string,
    inputRef: RefObject<HTMLElement>,
    onBlur: RefObject<NativeFocusEventHandler | undefined>
): {
    focused: boolean;
    onFocus: FocusEventHandler<HTMLElement>;
    onKeyDown: KeyboardEventHandler<HTMLInputElement>;
    onBottomSheetClose: VoidFunction;
} => {
    const [focused, setFocused] = useState(false);
    const actualInputValueRef = useRef(actualInputValue);
    actualInputValueRef.current = actualInputValue;
    const { isMobile } = useBreakpoint();

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            if (keyboardMatch(event.nativeEvent, keyboardKeys.ArrowUp)) {
                focusSuggestPicker(event, suggestContainerRef.current, 'up');
            }

            if (keyboardMatch(event.nativeEvent, keyboardKeys.ArrowDown)) {
                focusSuggestPicker(event, suggestContainerRef.current, 'down');
            }
        },
        [suggestContainerRef]
    );

    const onBottomSheetClose = useCallback(() => setFocused(false), [setFocused]);

    const onFocus = useCallback(() => {
        const escapeHandler = (event: KeyboardEvent) => {
            if (keyboardMatch(event, keyboardKeys.Escape)) {
                if (!isMobile) {
                    inputRef.current?.focus();
                }
                setValueSelectedFromSuggest(actualInputValueRef.current);
            }
        };

        const blurHandler = (event: FocusEvent) => {
            // Даем фокусу перейти на следующий элемент
            requestAnimationFrame(() => {
                if (
                    inputContainerRef.current?.contains(document.activeElement) ||
                    suggestContainerRef.current?.contains(document.activeElement) ||
                    document.activeElement === suggestContainerRef.current
                ) {
                    return;
                }
                setFocused(false);
                document.removeEventListener('blur', blurHandler, true);
                document.removeEventListener('keydown', escapeHandler, true);
                onBlur.current?.(event);
            });
        };

        // может быть focused если например перешли на другую вкладку
        // в этом случае диспатчится blur, но активным элементом остается инпут
        if (!focused) {
            document.addEventListener('blur', blurHandler, true);
            document.addEventListener('keydown', escapeHandler, true);
        }

        setFocused(true);
    }, [focused, setValueSelectedFromSuggest, inputContainerRef, suggestContainerRef, isMobile, inputRef, onBlur]);

    return { focused, onBottomSheetClose, onFocus, onKeyDown };
};
